import {action, computed, observable, runInAction} from 'mobx';
import {ComponentBaseController} from '@techsee/techsee-client-infra/lib/infra/ComponentBaseController';
import get from 'lodash/get';
import {RightBarSectionType} from '../../states/dashboard/dashboard-state/right-bar.state';
import {LibraryTreeController, LibraryTreeEvents} from './sections/library-section/library-tree/LibraryTree.controller';
import {ILibrarySectionController} from './sections/library-section/LibrarySection';
import {IMessagesSectionController} from './sections/messages-section/MessagesSection';
import {
    LibraryDialogController,
    LibraryDialogEvents
} from './sections/library-section/library-dialog/LibraryDialog.controller';
import {
    PredefinedTreeController,
    PredefinedTreeEvents
} from './sections/messages-section/predefined-tree/PredefinedTree.controller';
import {
    MessageFormController,
    MessageFormEvents
} from './sections/messages-section/message-form/MessageForm.controller';
import {
    SendTextDialogController,
    SendTextDialogEvents
} from './sections/messages-section/send-text-dialog/SendTextDialog.controller';
import {SearchTagsController} from './search-tags/SearchTagsController';
import {
    WifiScanHistoryController,
    WifiScanHistoryEvents
} from './sections/wifi-scan-history-section/WifiScanHistoryController';
import {VisualScanHistoryController} from './sections/visual-scan-history-section/VisualScanHistoryController';
import {
    ImagesGalleryController,
    HistoryGalleryController,
    IResourceGalleryDependencies
} from './sections/resource-gallery-section/ResourceGallerySectionController';
import {IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {EventLogService} from '../../services/event-log/event-log.service';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {DashboardState} from '../../states/dashboard/_contracts/DashboardState';
import {ITreeNode} from '@techsee/techsee-ui-common/lib/simple-tree-control-view';
import {IAccountData} from '@techsee/techsee-common/lib/constants/account.constants';

export enum RightBarEvents {
    OPEN_RIGHT_BAR = 'OPEN_RIGHT_BAR',
    CLOSE_RIGHT_BAR = 'CLOSE_RIGHT_BAR',
    LIBRARY_ITEM_SELECTED = 'LIBRARY_ITEM_SELECTED',
    LIBRARY_ITEM_SEND = 'LIBRARY_ITEM_SEND',
    SELECT_CLICKED_RESOURCE = 'SELECT_CLICKED_RESOURCE',
    IMAGE_LOADED = 'IMAGE_LOADED',
    UPLOAD_LOCAL_IMAGE = 'UPLOAD_LOCAL_IMAGE',
    FAILED_LOCAL_LOAD = 'FAILED_LOCAL_LOAD',
    OPEN_CUSTOMER_HISTORY_DIALOG = 'OPEN_CUSTOMER_HISTORY_DIALOG',
    TOGGLE_INTERACTION_SUMMARY_WIDGET = 'TOGGLE_INTERACTION_SUMMARY_WIDGET',
    LOAD_CUSTOMER_HISTORY = 'LOAD_CUSTOMER_HISTORY',
    SELECT_UNSELECT_ALL_RESOURCES = 'SELECT_UNSELECT_ALL_RESOURCES',
    DOWNLOAD_RESOURCES = 'DOWNLOAD_RESOURCES',
    COPY_RESOURCES_TO_CLIPBOARD = 'COPY_RESOURCES_TO_CLIPBOARD',
    SEND_RESOURCES_TO_CLIENT = 'SEND_RESOURCES_TO_CLIENT',
    SEND_RESOURCES_BY_EMAIL = 'SEND_RESOURCES_BY_EMAIL',
    DOWNLOAD_RESOURCES_AND_OPEN_EMAIL = 'DOWNLOAD_RESOURCES_AND_OPEN_EMAIL',
    TOGGLE_RESOURCE_SELECTION = 'TOGGLE_RESOURCE_SELECTION',
    HISTORY_RESOURCE_ACTION_CLICK = 'HISTORY_RESOURCE_ACTION_CLICK',
    HISTORY_RESOURCES_ON_SHARE_CLICK = 'HISTORY_RESOURCES_ON_SHARE_CLICK'
}

export interface MessageData {
    type: string;
    sender: string;
    isNew?: boolean;
    message: string;
    [key: string]: any;
}

export interface CurrentUser {
    accountId: string;
    _id: string;
    groups: any;
    role: string;
    [key: string]: any;
}

export class RightBarController extends ComponentBaseController {
    @observable private _isAsianLanguage: boolean = false;
    private _userType: {client: string; agent: string} = {client: 'client', agent: 'agent'};

    private _chatApi: any;
    private readonly dashboardState: DashboardState;
    private _sharingState: any;
    private readonly _useNewInterface: boolean;
    private readonly _dir: 'ltr' | 'rtl';

    private readonly _libraryTreeController: LibraryTreeController;
    private readonly _predefinedTreeController: PredefinedTreeController | null = null;
    public readonly messageFormController: MessageFormController;
    public readonly sendTextDialogController: SendTextDialogController;
    public readonly searchTagsController: SearchTagsController;
    public readonly wifiScanHistoryController: WifiScanHistoryController;
    public readonly visualScanHistoryController: VisualScanHistoryController;
    public readonly imagesGalleryController: ImagesGalleryController;
    public readonly historyGalleryController: HistoryGalleryController;
    public readonly libraryDialogController: LibraryDialogController;
    public readonly translate: (key: string) => string;

    constructor(
        chatApi: any,
        currentUser: CurrentUser,
        accountData: IAccountData,
        resourceService: IResourcesService,
        eventLog: EventLogService,
        trace: any,
        dashboardState: DashboardState,
        offlineMode: boolean = false,
        scanId: string = '',
        roomData: any = null,
        enableAgentWifiScanHistory: boolean = false,
        localeHelper: any,
        interfaceHelperService: any,
        translate: any
    ) {
        super();
        this._chatApi = chatApi;
        this.dashboardState = dashboardState;

        this._useNewInterface = interfaceHelperService?.useNewInterface || false;
        this._dir = localeHelper?.isRTL() ? 'rtl' : 'ltr';

        const isLibraryTaggingMode = offlineMode && currentUser.role == RolesConstant.KNOWLEDGE_EXPERT;
        const isWifiScanActive = get(roomData, 'isWifiScanActive', false);
        const isNativeFullAssist = get(accountData, 'protectedSettings.liveSdk.enableNativeFullAssist', false);
        const roomId = get(roomData, 'customerId', '');
        const rightBarStateInit = this.dashboardState.rightBarState;
        rightBarStateInit.libraryEnabled = get(accountData, 'settings.library', false);
        rightBarStateInit.allowMessages =
            !get(accountData, 'settings.disableChatMessages', false) && !isLibraryTaggingMode;
        rightBarStateInit.allowPredefined = get(accountData, 'settings.predefinedResources', false);
        this._isAsianLanguage = get(accountData, 'settings.language') === 'jp_JP';

        const hideHistory = !get(accountData, 'protectedSettings.showHistoryInSession', false) && !offlineMode;
        const searchTags =
            get(accountData, 'protectedSettings.imageTagging', false) &&
            get(accountData, 'protectedSettings.searchTags', false);
        const searchHistoryTags =
            get(accountData, 'protectedSettings.searchHistoryTags', false) && !isLibraryTaggingMode && !hideHistory;
        const searchLibraryTags = get(accountData, 'protectedSettings.searchLibraryTags', false);
        const isEnableAgentVisualScan =
            get(accountData, 'protectedSettings.connectivityGuru.enableVisualAnalysis', false) &&
            get(accountData, 'protectedSettings.liveSdk.enableNativeFullAssist', false);
        this.translate = translate || ((key: string) => key);
        this.messageFormController = new MessageFormController();
        this.sendTextDialogController = new SendTextDialogController();
        this.searchTagsController = new SearchTagsController(
            this._useNewInterface,
            searchTags,
            searchHistoryTags,
            searchLibraryTags,
            isLibraryTaggingMode
        );
        this.wifiScanHistoryController = new WifiScanHistoryController(
            this._useNewInterface,
            this._dir,
            this.dashboardState.isScreenMirroring,
            this.dashboardState.isVideoApplicationMode,
            enableAgentWifiScanHistory,
            this.dashboardState.shouldShowWifiScanHistoryInAllModes,
            isLibraryTaggingMode,
            get(accountData, '_id', ''),
            roomId,
            scanId
        );

        this.visualScanHistoryController = new VisualScanHistoryController(
            this._useNewInterface,
            this._dir,
            this.dashboardState.isScreenMirroring,
            this.dashboardState.isVideoApplicationMode,
            isEnableAgentVisualScan,
            isWifiScanActive,
            this.dashboardState.shouldShowWifiScanHistoryInAllModes,
            isLibraryTaggingMode,
            get(accountData, '_id', ''),
            roomId,
            scanId
        );

        this.messageFormController.on(MessageFormEvents.SEND_MESSAGE, (message: string) => {
            this.sendMessage(message);
        });

        this.sendTextDialogController.on(SendTextDialogEvents.SEND, (text: string) => {
            if (!this.observe) {
                this._chatApi.sendText(text, {predefined: true});
            }
        });

        this._libraryTreeController = new LibraryTreeController(
            currentUser.accountId,
            currentUser._id,
            currentUser.groups.toString(),
            isLibraryTaggingMode,
            resourceService,
            eventLog,
            trace,
            this._useNewInterface
        );

        if (this.dashboardState.rightBarState.allowPredefined) {
            this._predefinedTreeController = new PredefinedTreeController(
                currentUser.accountId,
                currentUser._id,
                currentUser.groups.toString(),
                resourceService,
                eventLog,
                trace,
                this._useNewInterface
            );
        }

        const self = this;
        const galleryDeps: IResourceGalleryDependencies = {
            translate: self.translate,
            getLocale: () => localeHelper?.getLocale() || 'en',
            useNewInterface: self._useNewInterface,
            dir: self._dir,
            toggleImageSection: self.toggleImageSection,
            toggleHistSection: self.toggleHistSection,
            getSelectedResourcesCount: self.getSelectedResourcesCount.bind(self),
            isSelfServiceResource: self.isSelfServiceResource.bind(self),
            getBadgeValue: self.getBadgeValue.bind(self)
        };

        this.imagesGalleryController = new ImagesGalleryController(galleryDeps, this.dashboardState);
        this.historyGalleryController = new HistoryGalleryController(galleryDeps, this.dashboardState);

        this.libraryDialogController = new LibraryDialogController(resourceService, offlineMode, false);

        this._libraryTreeController.on(LibraryTreeEvents.OPEN_LIBRARY_DIALOG, (node: ITreeNode) => {
            this.libraryDialogController.open(this._libraryTreeController.treeData as ITreeNode, node, 'library', true);
        });

        this.libraryDialogController.on(LibraryDialogEvents.SELECT_ITEM, (item: ITreeNode) => {
            this.emit(RightBarEvents.LIBRARY_ITEM_SELECTED, item);
        });

        this.libraryDialogController.on(LibraryDialogEvents.SEND_ITEM, (item: ITreeNode) => {
            this.emit(RightBarEvents.LIBRARY_ITEM_SEND, item);
        });

        this.libraryDialogController.on(LibraryDialogEvents.SYNC_SELECTION, (item: ITreeNode) => {
            if (this._libraryTreeController && item && item.state) {
                const currentlySelected = this._libraryTreeController.selectedNode;
                if (currentlySelected && currentlySelected.state) {
                    currentlySelected.state.selected = false;
                }
                item.state.selected = true;
            }
        });

        const imageOpen = offlineMode && !isLibraryTaggingMode;
        const videoOpen = offlineMode && !isLibraryTaggingMode;
        const libOpen = !this._libraryTreeController.empty && !imageOpen && !videoOpen;

        this.setImageSectionOpen(imageOpen);
        this.setVideoSectionOpen(videoOpen);
        this.setLibSectionOpen(libOpen);
    }

    get libraryTreeController(): LibraryTreeController {
        return this._libraryTreeController;
    }

    get predefinedTreeController(): PredefinedTreeController | null {
        return this._predefinedTreeController;
    }

    getLibraryTreeData(): ITreeNode | null {
        return this._libraryTreeController.treeData as ITreeNode | null;
    }

    setupWifiScanListeners(
        applyResultsCallback: (results: any, shouldShow: boolean) => void,
        updateScreenCallback: () => void
    ): void {
        this.wifiScanHistoryController.on(WifiScanHistoryEvents.APPLY_RESULTS, applyResultsCallback);
        this.wifiScanHistoryController.on(WifiScanHistoryEvents.UPDATE_SCREEN, updateScreenCallback);
    }

    setupVisualScanListeners(
        applyResultsCallback: (results: any, shouldShow: boolean) => void,
        updateScreenCallback: () => void
    ): void {
        this.visualScanHistoryController.on(WifiScanHistoryEvents.APPLY_RESULTS, applyResultsCallback);
        this.visualScanHistoryController.on(WifiScanHistoryEvents.UPDATE_SCREEN, updateScreenCallback);
    }

    async updateWifiScanHistory(refresh: boolean, accountId: string, userId: string, wifiScans: any[]): Promise<void> {
        return this.wifiScanHistoryController.updateWifiScanHistory(refresh, accountId, userId, wifiScans);
    }

    async updateVisualScanHistory(
        refresh: boolean,
        accountId: string,
        userId: string,
        visualScans: any[]
    ): Promise<void> {
        return this.visualScanHistoryController.updateWifiScanHistory(refresh, accountId, userId, visualScans);
    }

    searchInHistory(customerHistory: any, searchTerm: string): void {
        this.searchTagsController.searchInHistory(customerHistory, searchTerm);
    }

    findTaggedResources(resources: any[], searchTerm: string): any[] {
        return this.searchTagsController.findTaggedResources(resources, searchTerm);
    }

    @computed
    get libSectionOpen(): boolean {
        return this.dashboardState.rightBarState.libSectionOpen;
    }

    @computed
    get msgSectionOpen(): boolean {
        return this.dashboardState.rightBarState.msgSectionOpen;
    }

    @computed
    get libraryEnabled(): boolean {
        return this.dashboardState.rightBarState.libraryEnabled;
    }

    @computed
    get allowMessages(): boolean {
        return this.dashboardState.rightBarState.allowMessages;
    }

    @computed
    get allowPredefined(): boolean {
        return this.dashboardState.rightBarState.allowPredefined;
    }

    @computed
    get libraryIsEmpty(): boolean {
        return this._libraryTreeController.empty;
    }

    @computed
    get predefinedIsEmpty(): boolean {
        return !this._predefinedTreeController || this._predefinedTreeController.empty;
    }
    @computed
    get enableAgentWifiScanHistory(): boolean {
        return this.wifiScanHistoryController.enableAgentWifiScanHistory;
    }

    @computed
    get librarySectionController(): ILibrarySectionController {
        return {
            libraryTreeController: this._libraryTreeController,
            libraryEnabled: this.libraryEnabled,
            libSectionOpen: this.libSectionOpen,
            libraryIsEmpty: this.libraryIsEmpty,
            translate: this.translate,
            useNewInterface: this.useNewInterface,
            toggleLibSection: this.toggleLibSection
        };
    }

    @computed
    get messagesSectionController(): IMessagesSectionController {
        return {
            predefinedTreeController: this._predefinedTreeController,
            messageFormController: this.messageFormController,
            sendTextDialogController: this.sendTextDialogController,
            allowMessages: this.allowMessages,
            msgSectionOpen: this.msgSectionOpen,
            allowPredefined: this.allowPredefined,
            offlineMode: this.offlineMode,
            predefinedIsEmpty: this.predefinedIsEmpty,
            translate: this.translate,
            useNewInterface: this.useNewInterface,
            textMessages: this.textMessages,
            isAsianLanguage: this.isAsianLanguage,
            dir: this.dir,
            userType: this.userType,
            observe: this.observe,
            isFormDisabled: this.isFormDisabled,
            eventEmitter: {
                emit: (event: any, ...args: any[]) => {
                    this.emit(event, ...args);
                }
            },
            toggleMsgSection: this.toggleMsgSection
        };
    }

    @computed
    get imageSectionOpen(): boolean {
        return this.dashboardState.rightBarState.imageSectionOpen;
    }

    @computed
    get videoSectionOpen(): boolean {
        return this.dashboardState.rightBarState.videoSectionOpen;
    }

    @computed
    get useNewInterface(): boolean {
        return this._useNewInterface;
    }

    @computed
    get meetingHistory(): MessageData[] {
        return this.dashboardState.rightBarState.meetingHistory;
    }

    @computed
    get observe(): boolean {
        return this.dashboardState.isObserve;
    }

    @computed
    get offlineMode(): boolean {
        return this.dashboardState.isOfflineMode || this.dashboardState.offlineMode;
    }

    @computed
    get clientDisconnected(): boolean {
        return !this.dashboardState.clientRoomStorage.connected;
    }

    @computed
    get offlineRoom(): boolean {
        return this.dashboardState.isOfflineRoom;
    }

    @computed
    get isAsianLanguage(): boolean {
        return this._isAsianLanguage;
    }

    @computed
    get dir(): 'ltr' | 'rtl' {
        return this._dir;
    }

    @computed
    get userType(): {client: string; agent: string} {
        return this._userType;
    }

    @computed
    get meetingHistoryLength(): number {
        return this.dashboardState.rightBarState.meetingHistoryLength;
    }

    @computed
    get textMessages(): MessageData[] {
        return this.dashboardState.rightBarState.textMessages;
    }

    @computed
    get isFormDisabled(): boolean {
        const enableIdleModeUsability = get(
            (this.dashboardState as any).accountData,
            'protectedSettings.enableIdleModeUsability',
            false
        );
        return (
            (this.clientDisconnected && !this.offlineRoom) ||
            (this.dashboardState.displayLoaderReconnecting && enableIdleModeUsability && !this.offlineRoom)
        );
    }

    findMessageByStorageIndex(storageIndex: string): MessageData | undefined {
        return this.dashboardState.rightBarState.meetingHistory.find((msg) => msg.storageIndex === storageIndex);
    }

    getMessageCountByType(type: string): number {
        return this.dashboardState.rightBarState.meetingHistory.filter((m) => m.type === type).length;
    }

    @action
    toggleSection(section: RightBarSectionType): void {
        const rightBarState = this.dashboardState.rightBarState;
        const isMainSection = ['lib', 'msg', 'image', 'video', 'hist'].includes(section);
        const currentState = this.getSectionOpen(section);
        const newState = !currentState;

        if (isMainSection && newState) {
            runInAction(() => {
                rightBarState.libSectionOpen = false;
                rightBarState.msgSectionOpen = false;
                rightBarState.imageSectionOpen = false;
                rightBarState.videoSectionOpen = false;
                rightBarState.histSectionOpen = false;
            });
        }

        this.setSectionOpen(section, newState);
    }

    getSectionOpen(section: RightBarSectionType): boolean {
        const rightBarState = this.dashboardState.rightBarState;
        switch (section) {
            case 'lib':
                return rightBarState.libSectionOpen;
            case 'msg':
                return rightBarState.msgSectionOpen;
            case 'image':
                return rightBarState.imageSectionOpen;
            case 'video':
                return rightBarState.videoSectionOpen;
            case 'hist':
                return rightBarState.histSectionOpen;
            case 'wifiScan':
                return rightBarState.wifiScanSectionOpen;
            case 'visualScan':
                return rightBarState.visualScanSectionOpen;
            default:
                return false;
        }
    }

    @action
    setSectionOpen(section: RightBarSectionType, open: boolean): void {
        const rightBarState = this.dashboardState.rightBarState;
        switch (section) {
            case 'lib':
                rightBarState.libSectionOpen = open;
                break;
            case 'msg':
                rightBarState.msgSectionOpen = open;
                break;
            case 'image':
                rightBarState.imageSectionOpen = open;
                break;
            case 'video':
                rightBarState.videoSectionOpen = open;
                break;
            case 'hist':
                rightBarState.histSectionOpen = open;
                break;
            case 'wifiScan':
                rightBarState.wifiScanSectionOpen = open;
                break;
            case 'visualScan':
                rightBarState.visualScanSectionOpen = open;
                break;
        }
    }

    @action
    toggleLibSection = (): void => {
        this.toggleSection('lib');
    };

    @action
    setLibSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('lib', isOpen);
    };

    @action
    toggleMsgSection = (): void => {
        this.toggleSection('msg');
    };

    @action
    setMsgSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('msg', isOpen);
    };

    @action
    setImageSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('image', isOpen);
    };

    ensureImageSectionOpenForResource(resource: any): void {
        if (
            this.dashboardState.offlineMode &&
            resource &&
            !resource.isVideo &&
            resource.url &&
            !this.dashboardState.rightBarState.imageSectionOpen
        ) {
            this.setImageSectionOpen(true);
        }
    }

    @action
    setVideoSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('video', isOpen);
    };

    @action
    setHistSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('hist', isOpen);
    };

    @action
    setWifiScanSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('wifiScan', isOpen);
    };

    @action
    setVisualScanSectionOpen = (isOpen: boolean): void => {
        this.setSectionOpen('visualScan', isOpen);
    };

    setupLibraryEventListeners(
        selectTool: (tool: string, clearCanvas: boolean) => void,
        setResource: (resource: any) => void,
        setPreloadedResource: (resource: any) => void,
        loadTextMessage: (message: string) => void,
        setMsgSectionOpen: (isOpen: boolean) => void,
        setHideWidgets: (hide: boolean) => void,
        setHideWifiScan: (hide: boolean) => void,
        hideWifiAnalyzerScan: () => void
    ): void {
        this._libraryTreeController.on(LibraryTreeEvents.SELECT_TOOL, selectTool);
        this._libraryTreeController.on(LibraryTreeEvents.SET_RESOURCE, setResource);
        this._libraryTreeController.on(LibraryTreeEvents.SET_PRELOADED_RESOURCE, setPreloadedResource);
        this._libraryTreeController.on(LibraryTreeEvents.LOAD_TEXT_MESSAGE, loadTextMessage);
        this._libraryTreeController.on(LibraryTreeEvents.SET_MSG_SECTION_OPEN, setMsgSectionOpen);
        this._libraryTreeController.on(LibraryTreeEvents.SET_HIDE_WIDGETS, setHideWidgets);
        this._libraryTreeController.on(LibraryTreeEvents.SET_HIDE_WIFI_SCAN, setHideWifiScan);
        this._libraryTreeController.on(LibraryTreeEvents.HIDE_WIFI_ANALYZER_SCAN, hideWifiAnalyzerScan);
    }

    setupPredefinedEventListeners(openTextDialog: (message: string) => void): void {
        if (this._predefinedTreeController) {
            this._predefinedTreeController.on(PredefinedTreeEvents.OPEN_TEXT_DIALOG, (message: string) => {
                const readonly = this.observe || this.offlineMode;
                this.sendTextDialogController.open(message, readonly, this.clientDisconnected);
            });
        }
    }

    openTextDialog = (placeholder: string): void => {
        const readonly = this.observe || this.offlineMode;
        this.sendTextDialogController.open(placeholder, readonly, this.clientDisconnected);
    };

    setClientDisconnected = (clientDisconnected: boolean): void => {
        this.libraryDialogController.setEndMeeting(clientDisconnected);
    };

    setUserType = (userType: {client: string; agent: string}): void => {
        this._userType = userType;
    };

    setIsScreenMirroring = (): void => {
        const isScreenMirroring = this.dashboardState.isScreenMirroring;
        this.wifiScanHistoryController.setIsScreenMirroring(isScreenMirroring);
        this.visualScanHistoryController.setIsScreenMirroring(isScreenMirroring);
    };

    setIsVideoApplicationMode = (): void => {
        const isVideoApplicationMode = this.dashboardState.isVideoApplicationMode;
        this.wifiScanHistoryController.setIsVideoApplicationMode(isVideoApplicationMode);
        this.visualScanHistoryController.setIsVideoApplicationMode(isVideoApplicationMode);
    };

    @action
    pushMessage = (message: MessageData): void => {
        this.dashboardState.rightBarState.meetingHistory = [
            ...this.dashboardState.rightBarState.meetingHistory,
            message
        ];
    };

    @action
    setMeetingHistory = (history: MessageData[]): void => {
        this.dashboardState.rightBarState.meetingHistory = history ? [...history] : [];
    };

    @action
    updateMessage = (storageIndex: string, updates: Partial<MessageData>): void => {
        this.dashboardState.rightBarState.meetingHistory = this.dashboardState.rightBarState.meetingHistory.map(
            (msg) => (msg.storageIndex === storageIndex ? {...msg, ...updates} : msg)
        );
    };

    @action
    clearMeetingHistory = (): void => {
        this.dashboardState.rightBarState.meetingHistory = [];
    };

    @action
    setExpandRightBar = (expand: boolean): void => {
        this.dashboardState.rightBarState.expandRightBar = expand;
    };

    @action
    setCustomerHistory = (history: any): void => {
        this.dashboardState.rightBarState.customerHistory = history ? {...history} : null;
    };

    @action
    setEnableHistory = (enabled: boolean): void => {
        this.dashboardState.rightBarState.enableHistory = enabled;
    };

    @action
    setHideHistory = (hide: boolean): void => {
        this.dashboardState.rightBarState.hideHistory = hide;
    };

    @action
    setLibraryTaggingMode = (mode: boolean): void => {
        this.dashboardState.rightBarState.libraryTaggingMode = mode;
    };

    @action
    setResourcesReadyToBeShared = (ready: boolean): void => {
        this.dashboardState.rightBarState.resourcesReadyToBeShared = ready;
    };

    @action
    setSelectedResourcesCount = (type: 'images' | 'history', count: number): void => {
        this.dashboardState.rightBarState.selectedResourcesCount[type] = count;
    };

    @action
    setSharingModeActions = (actions: any[]): void => {
        this.dashboardState.rightBarState.sharingModeActions = Array.from(actions || []);
    };

    @action
    setInteractionSummaryPreviews = (previews: any[]): void => {
        this.dashboardState.rightBarState.interactionSummaryPreviews = Array.from(previews || []);
    };

    @action
    setDateFormat = (format: string): void => {
        this.dashboardState.rightBarState.dateFormat = format || '';
    };

    @action
    setAllowUploadImageFileFromDashboard = (allow: boolean): void => {
        this.dashboardState.rightBarState.allowUploadImageFileFromDashboard = allow;
    };

    @action
    setLibraryEnabled = (enabled: boolean): void => {
        this.dashboardState.rightBarState.libraryEnabled = enabled;
    };

    @action
    setAllowMessages = (allow: boolean): void => {
        this.dashboardState.rightBarState.allowMessages = allow;
    };

    @action
    setAllowPredefined = (allow: boolean): void => {
        this.dashboardState.rightBarState.allowPredefined = allow;
    };

    sendMessage = (message: string): void => {
        if (!message || !message.trim()) {
            return;
        }
        this._chatApi.sendText(message);
    };

    openRightBar = (isInputTextFocus?: boolean): void => {
        this.emit(RightBarEvents.OPEN_RIGHT_BAR, isInputTextFocus);
    };

    closeRightBar = (isInputTextFocus?: boolean): void => {
        this.emit(RightBarEvents.CLOSE_RIGHT_BAR, isInputTextFocus);
    };

    handlePushWifiScan(scan: any): void {
        if (this.wifiScanHistoryController) {
            this.wifiScanHistoryController.handleWifiScanResults(scan);
        }
    }

    handlePushVisualScan(scan: any): void {
        if (this.visualScanHistoryController) {
            this.visualScanHistoryController.handleWifiScanResults(scan);
        }
    }

    getExistingScanIds(): string[] {
        const existingScanIds: string[] = [];
        if (this.wifiScanHistoryController) {
            const wifiScans = this.wifiScanHistoryController.wifiScanHistoryData;
            wifiScans.forEach((scan) => {
                const scanId = scan.id || scan._id;
                if (scanId && !existingScanIds.includes(scanId)) {
                    existingScanIds.push(scanId);
                }
            });
        }

        if (this.visualScanHistoryController) {
            const visualScans = this.visualScanHistoryController.visualScanHistoryData;
            visualScans.forEach((scan) => {
                const scanId = scan.id || scan._id;
                if (scanId && !existingScanIds.includes(scanId)) {
                    existingScanIds.push(scanId);
                }
            });
        }

        return existingScanIds;
    }

    imagesPreviewWidth(): number {
        return this.useNewInterface ? 110 : 95;
    }

    imagesPreviewHeight(): number {
        return this.useNewInterface ? 140 : 120;
    }

    isSelfServiceResource(resource: any): boolean {
        const eveAnalysis = resource && resource.extra && resource.extra.eveAnalysis;
        const analysisOriginValue = 30; // analysisOrigin.comcast value from constants

        return eveAnalysis && (eveAnalysis.panel || eveAnalysis.origin === analysisOriginValue);
    }

    getBadgeValue(image: any): string | number {
        let badgeValue: string | number = image.resourceIndex || 0;

        if (this.isSelfServiceResource(image)) {
            const panelKey =
                image.extra.eveAnalysis.panel === 'CABLE'
                    ? image.extra.eveAnalysis.deviceName
                    : image.extra.eveAnalysis.panel;

            badgeValue += ' ' + this.translate(`MAIN.VIEW.ANALYZE_SUMMARY.${panelKey}`);
        }

        return badgeValue;
    }

    getImageLayout(image: any): string {
        if (this.isSelfServiceResource(image)) {
            return 'self-service'; // ImageShareLayouts.SELF_SERVICE
        }

        return 'default'; // ImageShareLayouts.DEFAULT
    }

    @computed
    get screenshots(): any[] {
        return this.dashboardState?.screenshots || [];
    }

    @computed
    get hasScreenshots(): boolean {
        return this.dashboardState?.hasScreenshots || false;
    }

    @computed
    get showMultipleResourcesShareForImages(): boolean {
        return this.dashboardState?.showMultipleResourcesShareForImages || false;
    }

    @computed
    get expandRightBar(): boolean {
        return this.dashboardState.rightBarState.expandRightBar;
    }

    setSharingState(sharingState: any): void {
        this._sharingState = sharingState;
    }

    getSelectedResourcesCount(type: 'images' | 'history'): number {
        return this._sharingState?.selectedResources?.[type]?.length || 0;
    }

    @computed
    get resourcesReadyToBeShared(): boolean {
        return this.dashboardState.rightBarState.resourcesReadyToBeShared;
    }

    selectClickedResource(resource: any, roomId?: string): void {
        this.emit(RightBarEvents.SELECT_CLICKED_RESOURCE, {resource, roomId});
    }

    isActiveResource(resource: any): boolean {
        if (!resource || !this.dashboardState.currentResource) {
            return false;
        }
        return (
            resource.url === this.dashboardState.currentResource.url ||
            resource._id === this.dashboardState.currentResource._id
        );
    }

    imageLoaded(event: any): void {
        this.emit(RightBarEvents.IMAGE_LOADED, event);
    }

    uploadLocalImage(file: any): void {
        this.emit(RightBarEvents.UPLOAD_LOCAL_IMAGE, file);
    }

    failedLocalLoad(error: any): void {
        this.emit(RightBarEvents.FAILED_LOCAL_LOAD, error);
    }

    openCustomerHistoryDialog(): void {
        this.emit(RightBarEvents.OPEN_CUSTOMER_HISTORY_DIALOG);
    }

    toggleInteractionSummaryWidget(data: any): void {
        this.emit(RightBarEvents.TOGGLE_INTERACTION_SUMMARY_WIDGET, data);
    }

    displayHistory(): void {
        const rightBarState = this.dashboardState.rightBarState;

        if (
            !rightBarState.enableHistory ||
            rightBarState.videoSectionOpen ||
            rightBarState.hideHistory ||
            rightBarState.libraryTaggingMode
        ) {
            runInAction(() => {
                this.setHistSectionOpen(false);
            });
            return;
        }

        this.emit(RightBarEvents.LOAD_CUSTOMER_HISTORY, {refresh: false});
    }

    areAllResourcesSelected(type: string, data: {history: any[]; images: any[]}): boolean {
        const resourceType = type as 'images' | 'history';
        const allResources = data[resourceType] || [];
        const selectedCount = this.getSelectedResourcesCount(resourceType);
        return allResources.length > 0 && selectedCount > 0 && allResources.length === selectedCount;
    }

    selectUnselectAllResources(resourceType: string, data: {history: any[]; images: any[]}, select?: boolean): void {
        this.emit(RightBarEvents.SELECT_UNSELECT_ALL_RESOURCES, {resourceType, data, select});
    }

    @computed
    get enableLocalImageSaving(): boolean {
        return this.dashboardState.enableLocalImageSaving;
    }

    downloadResources(resourceType: string, data: {history: any[]; images: any[]}): void {
        this.emit(RightBarEvents.DOWNLOAD_RESOURCES, {resourceType, data});
    }

    @computed
    get enableCopyToClipboard(): boolean {
        return this.dashboardState.enableCopyToClipboard;
    }

    copyResourcesToClipboard(resourceType: string): void {
        this.emit(RightBarEvents.COPY_RESOURCES_TO_CLIPBOARD, resourceType);
    }

    sendResourcesToClient(resourceType: string): void {
        this.emit(RightBarEvents.SEND_RESOURCES_TO_CLIENT, resourceType);
    }

    @computed
    get downloadAndEmailSharing(): boolean {
        return this.dashboardState.downloadAndEmailSharing;
    }

    @computed
    get enableEmailImageSharing(): boolean {
        return this.dashboardState.enableEmailImageSharing;
    }

    sendResourcesByEmail(resourceType: string): void {
        this.emit(RightBarEvents.SEND_RESOURCES_BY_EMAIL, resourceType);
    }

    downloadResourcesAndOpenEmail(resourceType: string, data: {history: any[]; images: any[]}): void {
        this.emit(RightBarEvents.DOWNLOAD_RESOURCES_AND_OPEN_EMAIL, {resourceType, data});
    }

    toggleResourceSelection(resourceType: string, resource: any): void {
        this.emit(RightBarEvents.TOGGLE_RESOURCE_SELECTION, {resourceType, resource});
    }

    isResourceSelected(resource: any, type: string): boolean {
        if (!resource || !resource.url) {
            return false;
        }
        const resourceType = type as 'images' | 'history';
        const selectedResources = this._sharingState?.selectedResources?.[resourceType];
        if (!selectedResources || selectedResources.length === 0) {
            return false;
        }
        return selectedResources.some((selectedResource: any) => selectedResource.url === resource.url);
    }

    @computed
    get enableSharingImagesContainer(): boolean {
        return this.dashboardState?.enableSharingImagesContainer || false;
    }

    @computed
    get isQuickShareEnabled(): boolean {
        return this.dashboardState.isQuickShareEnabled;
    }

    historyResourcesOnShareClick(e: any, data: any): void {
        this.emit(RightBarEvents.HISTORY_RESOURCES_ON_SHARE_CLICK, {e, data});
    }

    historyResourceActionClick(e: any, type: string, data: any): void {
        this.emit(RightBarEvents.HISTORY_RESOURCE_ACTION_CLICK, {e, type, data});
    }

    isSelectResourceReady(data: any): boolean {
        if (!this._sharingState?.imagesWidgets) {
            return false;
        }

        const location = data?._sharingLocation;
        const resource = [...this._sharingState.imagesWidgets].find((widget: any) => {
            const urlMatch = widget.url === data?.url;
            const locationMatch = widget._sharingLocation === location;
            return urlMatch && locationMatch;
        });

        return !!(resource && resource.inProgress === false);
    }

    // Cached MobX observables
    @computed
    get flattenedHistoryRecords(): any[] {
        return this.dashboardState.rightBarState.flattenedHistoryRecords;
    }

    @computed
    get historyEmpty(): boolean {
        return this.dashboardState.rightBarState.historyEmpty;
    }

    @computed
    get customerHistory(): any {
        return this.dashboardState.rightBarState.customerHistory;
    }

    @computed
    get interactionSummaryPreviews(): any[] {
        return this.dashboardState.rightBarState.interactionSummaryPreviews;
    }

    @computed
    get dateFormat(): string {
        return this.dashboardState.rightBarState.dateFormat;
    }

    @computed
    get multipleImagesSharingEnabled(): boolean {
        return this.dashboardState?.multipleImagesSharingEnabled || false;
    }

    @computed
    get isHighImageSharingQuality(): boolean {
        return this.dashboardState?.isHighImageSharingQuality || false;
    }

    @computed
    get enableHistory(): boolean {
        return this.dashboardState.rightBarState.enableHistory;
    }

    @computed
    get histSectionOpen(): boolean {
        return this.dashboardState.rightBarState.histSectionOpen;
    }

    // Removed update* methods - accessing rightBarState directly

    @action
    toggleImageSection = (): void => {
        this.toggleSection('image');
    };

    @action
    toggleHistSection = (): void => {
        this.toggleSection('hist');
    };

    @computed
    get sharingModeActions(): any[] {
        return this.dashboardState.rightBarState.sharingModeActions;
    }

    @computed
    get hideHistory(): boolean {
        return this.dashboardState.rightBarState.hideHistory;
    }

    @computed
    get libraryTaggingMode(): boolean {
        return this.dashboardState.rightBarState.libraryTaggingMode;
    }

    @computed
    get allowUploadImageFileFromDashboard(): boolean {
        return this.dashboardState.rightBarState.allowUploadImageFileFromDashboard;
    }

    // Sync method - called by main controller to update cached values
    // Main controller should call this when values change or after setting up callbacks

    dispose(): void {
        this._libraryTreeController.dispose();
        if (this._predefinedTreeController) {
            this._predefinedTreeController.dispose();
        }
        this.messageFormController.removeAllListeners();
        this.sendTextDialogController.dispose();
        this.libraryDialogController.dispose();
        this.searchTagsController.dispose();
        this.wifiScanHistoryController.dispose();
        super.dispose();
    }
}
